import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import { uploadEtimsInvoice } from "./files";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type Payout = Schemas["StaffPayoutRequest"];
export type PayoutStatus = Payout["status"];
export type Attempt = Payout["attempts"][number];

export const PAYOUT_STATUS: Record<PayoutStatus, [label: string, tone: Tone]> = {
  submitted: ["To review", "warning"],
  approved: ["To pay", "info"],
  paid: ["Paid", "success"],
  rejected: ["Rejected", "neutral"],
  cancelled: ["Cancelled", "neutral"],
};

/** An attempt M-Pesa hasn't answered yet: paying again is refused until it's settled or marked failed. */
export const inFlight = (a: Attempt) => a.status === "pending" || a.status === "processing";

/** The badge: its status, or what its last payment attempt says while it's still to pay. */
export function payoutBadge(p: Pick<Payout, "status" | "attempts">): [string, Tone] {
  const last = p.attempts.at(-1);
  if (p.status === "approved" && last && inFlight(last)) return ["Paying", "info"];
  if (p.status === "approved" && last?.status === "failed") return ["Payment failed", "danger"];
  return PAYOUT_STATUS[p.status];
}

/**
 * What a request offers. Review while submitted; pay once approved, unless an attempt is still
 * waiting for M-Pesa (`PAYOUT_IN_PROGRESS`): that one can be marked failed instead. While the
 * partner's withdrawals are blocked it can be rejected but not approved or paid
 * (`PAYOUTS_BLOCKED`). The eTIMS invoice can be attached until it's settled. The API decides the
 * rest (a reviewer can't review their own request, nor approve one missing a required invoice).
 */
export function payoutActionsFor(
  p: Pick<Payout, "status" | "attempts" | "payout_method"> & Partial<Pick<Payout, "payouts_blocked">>,
) {
  const waiting = p.attempts.some(inFlight);
  const blocked = Boolean(p.payouts_blocked);
  return {
    review: p.status === "submitted",
    approve: p.status === "submitted" && !blocked,
    pay: p.status === "approved" && !waiting && !blocked,
    payByMpesa: p.status === "approved" && !waiting && !blocked && p.payout_method === "mpesa",
    markFailed: p.status === "approved" && waiting,
    attachInvoice: p.status === "submitted" || p.status === "approved",
  };
}

export const payoutQuery = (id: string) =>
  queryOptions({
    queryKey: ["payout", id],
    queryFn: () => ok(api.GET("/v1/admin/wallets/payouts/{id}", { params: { path: { id } } })),
  });

const path = (id: string) => ({ params: { path: { id } } });

export const payoutActions = {
  approve: (id: string, notes?: string) =>
    ok(api.POST("/v1/admin/wallets/payouts/{id}/approve", { ...path(id), body: { notes } })),
  reject: (id: string, notes?: string) =>
    ok(api.POST("/v1/admin/wallets/payouts/{id}/reject", { ...path(id), body: { notes } })),
  /** No reference: an M-Pesa B2C payment. A reference: it was paid another way, and is marked paid now. */
  pay: (id: string, reference?: string) =>
    ok(api.POST("/v1/admin/payments/payouts/{id}/pay", { ...path(id), body: { reference } })),
  /** Uploads the partner's eTIMS invoice (a PDF) and attaches it, replacing any before. */
  attachInvoice: async (id: string, file: File) => {
    const stored = await uploadEtimsInvoice(file);
    return ok(api.PUT("/v1/admin/wallets/payouts/{id}/invoice", { ...path(id), body: { file_id: stored.id } }));
  },
  failAttempt: (id: string, paymentId: string, reason: string) =>
    ok(
      api.POST("/v1/admin/payments/payouts/{id}/attempts/{payment_id}/fail", {
        params: { path: { id, payment_id: paymentId } },
        body: { reason },
      }),
    ),
};
